
import { EventFilters } from "@/components/EventFilters";
import { HeroBanner } from "@/components/HeroBanner";
import { RestaurantList } from "@/components/RestaurantList";
import { ViewToggle } from "@/components/ViewToggle";
import { FeaturedRestaurants } from "@/components/FeaturedRestaurants";
import { useState, lazy, Suspense } from "react";
import { useRestaurantFilters } from "@/hooks/useRestaurantFilters";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/contexts/AuthContext";
import { useFavorites } from "@/hooks/useFavorites";
import { RestaurantListContent } from "@/components/restaurant-list/RestaurantListContent";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";
import { RestaurantSubmissionDialog } from "@/components/RestaurantSubmissionDialog";
import { Link } from "react-router-dom";
import { MapPin, BookOpen } from "lucide-react";

const RestaurantsMapbox = lazy(() => import("@/components/RestaurantsMapbox").then(m => ({ default: m.RestaurantsMapbox })));

const Index = () => {
  console.log('Index - Component mounted');
  
  const [view, setView] = useState<"list" | "map">("list");
  const [currentTab, setCurrentTab] = useState<"all" | "favorites">("all");
  const [filters, setFilters] = useState({
    neighborhood: '',
    eventType: '',
    groupSize: '',
    priceRange: '',
    search: ''
  });

  const { session } = useAuth();
  const { favorites, isLoading: isFavoritesLoading } = useFavorites();
  const { data: restaurants = [], isLoading } = useRestaurantFilters(filters);
  const isMobile = useIsMobile();

  const favoritedRestaurants = restaurants.filter(restaurant => 
    favorites?.includes(restaurant.id)
  );

  const handleFilterChange = (type: string, value: string) => {
    console.log('Index - Filter changed:', { type, value });
    setFilters(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleSearch = (query: string) => {
    console.log('Index - Search query:', query);
    setFilters(prev => ({
      ...prev,
      search: query
    }));
  };

  const handleClearFilters = () => {
    console.log('Index - Clearing all filters');
    setFilters({
      neighborhood: '',
      eventType: '',
      groupSize: '',
      priceRange: '',
      search: ''
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>NYC Private Event Spaces | Find Restaurant Venues for Events</title>
        <meta name="description" content="Discover the perfect NYC restaurant venue for your next private event. Browse private dining rooms, event spaces, and party venues for corporate events, celebrations, and more." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="NYC Private Event Spaces | Find Restaurant Venues for Events" />
        <meta property="og:description" content="Discover the perfect NYC restaurant venue for your next private event. Browse private dining rooms, event spaces, and party venues for corporate events, celebrations, and more." />
        <meta property="og:image" content="/og-image.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NYC Private Event Spaces | Find Restaurant Venues for Events" />
        <meta name="twitter:description" content="Discover the perfect NYC restaurant venue for your next private event. Browse private dining rooms, event spaces, and party venues for corporate events, celebrations, and more." />
        <meta name="twitter:image" content="/og-image.png" />
        
        {/* Additional SEO tags */}
        <meta name="keywords" content="NYC private dining, restaurant event spaces, private party venues, corporate event venues, private dining rooms NYC, restaurant venues, private events" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://onthetown.nyc" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "On The Town",
            "url": "https://onthetown.nyc",
            "description": "Discover the perfect NYC restaurant venue for your next private event.",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://onthetown.nyc/?search={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>

      <HeroBanner onSearch={handleSearch} />
      
      <main className="flex-1 flex flex-col">
        {/* Featured section */}
        <section className="container mx-auto px-4 lg:px-8 py-5 md:py-7">
          <FeaturedRestaurants />
        </section>
        
        {/* Divider */}
        <div className="border-t border-border/40" />
        
        {/* Filter & Results section */}
        <section className="container mx-auto px-4 lg:px-8 py-4 md:py-6 flex-1 flex flex-col">
          <div className={`space-y-4 flex-1 flex flex-col ${view === "map" ? "overflow-hidden" : ""}`}>
            {/* Toolbar */}
            <div className="space-y-3 md:space-y-0">
              <div className="flex items-center justify-between gap-4">
                <h2 className="font-display text-xl md:text-2xl font-semibold text-foreground whitespace-nowrap">
                  All Venues
                </h2>
                <div className="flex items-center gap-3">
                  <RestaurantSubmissionDialog />
                  <ViewToggle view={view} onViewChange={setView} />
                </div>
              </div>
              <Link 
                to="/neighborhoods" 
                className="inline-flex items-center gap-1.5 text-sm font-medium text-secondary hover:text-secondary/80 transition-colors"
              >
                <MapPin className="w-3.5 h-3.5" />
                Browse venues by neighborhood →
              </Link>
            </div>
            {/* Filters */}
            <EventFilters 
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />
            
            {/* Content */}
            <div className={`relative flex-1 min-h-0 ${view === "map" ? "flex" : ""}`}>
              {session ? (
                <Tabs defaultValue="all" className="w-full" onValueChange={(value) => setCurrentTab(value as "all" | "favorites")}>
                  <TabsList>
                    <TabsTrigger value="all">All Restaurants</TabsTrigger>
                    <TabsTrigger value="favorites">
                      My Favorites ({favoritedRestaurants.length})
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="all" className="mt-4">
                    <div className={`transition-opacity duration-300 ${view === "list" ? 'opacity-100' : 'opacity-0'}`}>
                      <RestaurantList filters={filters} />
                    </div>
                  </TabsContent>
                  <TabsContent value="favorites" className="mt-4">
                    <div className={`transition-opacity duration-300 ${view === "list" ? 'opacity-100' : 'opacity-0'}`}>
                      {isFavoritesLoading ? (
                        <div className="text-center py-8">Loading favorites...</div>
                      ) : favoritedRestaurants.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                          <p className="font-display text-lg">No favorites yet</p>
                          <p className="mt-2 text-sm">Click the heart icon on any venue to save it here.</p>
                        </div>
                      ) : (
                        <RestaurantListContent restaurants={favoritedRestaurants} />
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              ) : (
                <div className={`transition-opacity duration-300 ${view === "list" ? 'opacity-100' : 'opacity-0'}`}>
                  <RestaurantList filters={filters} />
                </div>
              )}
              
              <Suspense fallback={null}>
                <div className={`${view === "map" ? "absolute inset-0" : "sticky bottom-4"}`}>
                  <RestaurantsMapbox 
                    restaurants={session && currentTab === "favorites" ? favoritedRestaurants : restaurants} 
                    isVisible={view === "map"} 
                  />
                </div>
              </Suspense>
            </div>
          </div>
        </section>

        {/* Blog CTA */}
        <section className="border-t border-border/40 bg-accent/50">
          <div className="container mx-auto px-4 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <BookOpen className="w-5 h-5 text-secondary" />
              <p className="text-sm text-muted-foreground">
                Planning an event? Read our guides on choosing the perfect venue.
              </p>
            </div>
            <Link 
              to="/blog" 
              className="text-sm font-medium text-secondary hover:text-secondary/80 transition-colors whitespace-nowrap"
            >
              Read our blog →
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Index;
